import React, { useContext, useState } from 'react';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import jwt_decode from 'jwt-decode';
import moment from 'moment';
import { isEmpty } from 'lodash';
import Cookies from 'universal-cookie/lib/Cookies';
import { PaymentStatusDtoSubscriptionTypeEnum } from '@apari/core-api/models/payment-status-dto';
import {
    AuthenticationApi,
    CurrentUserDto,
    GoogleAuthenticatorActivationData,
    JwtRequest,
    JwtResponseApplicationTypeEnum,
    OauthApi,
    OAuthRequestProviderEnum,
    PaymentStatusDto,
    PaymentStatusDtoStatusEnum,
    SignUpData,
    SubscriptionBooking,
    TwoFactorAuthData,
    TwoFactorAuthDataTwoFactorAuthMethodEnum,
    TwoFactorAuthenticationApi,
    UserApi
} from '@apari/core-api';
import { GLOBAL } from 'constants/index';
import { GlobalServices, Localisation, LocalStorageServices } from 'utils';
import { encrypt } from 'utils/encryption';
import { NotificationTypes, RoleTypes, SubscriptionTypeValues, TOUR_TYPES, ToursStatus } from 'types';
import { AppContext } from './AppContext';
import { CURRENT_USER_INFO } from '../constants/mockData';

const twoFactorAuthenticationApi = new TwoFactorAuthenticationApi();
const authenticationApi = new AuthenticationApi();
const oauth2Api = new OauthApi();
const userApi = new UserApi();

interface AuthenticationContextInterface {
    authenticated: boolean;
    user: CurrentUserDto;
    signUpEmail: string;
    signUpDialogOpen: boolean;
    confirmationDialogOpen: boolean;
    verificationEmailSent: boolean;
    bookedSubscriptionPackages?: SubscriptionBooking;
    paymentStatus?: PaymentStatusDto;
    setPaymentStatus: React.Dispatch<React.SetStateAction<PaymentStatusDto | undefined>>;
    googleTwoFactorAuthenticationData: GoogleAuthenticatorActivationData;
    setUser: React.Dispatch<React.SetStateAction<CurrentUserDto>>;
    setSignUpDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setConfirmationDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setVerificationEmailSent: React.Dispatch<React.SetStateAction<boolean>>;
    bookingDataLoading: boolean;
    finishingWizardLoading: boolean;
    toursStatus?: ToursStatus;
    setToursStatus: React.Dispatch<React.SetStateAction<ToursStatus | undefined>>;
    setBookingDataLoading: React.Dispatch<React.SetStateAction<boolean>>;
    signInCalled: boolean;
    setSignInCalled: React.Dispatch<React.SetStateAction<boolean>>;
    setGoogleTwoFactorAuthenticationData: React.Dispatch<React.SetStateAction<GoogleAuthenticatorActivationData>>;
    setCurrentUser: (ignoreTour?: boolean, ignoreLoading?: boolean) => Promise<void>;
    setMtdWizardDone: () => void;
    setAuthToken: (token: string | undefined) => void;
    signUp: (user: SignUpData) => void;
    signOut: () => void;
    signIn: (user: JwtRequest, history: any) => void;
    handleOAuthLogin: (provider: OAuthRequestProviderEnum, code: string, state: string, history: any) => void;
    confirmTwoFactorAuthentication: (code: string, redirectUrl: string, history: any, callbackFunction?: () => void) => void;
    resendTwoFactorCode: () => void;
    resendEmailVerificationToken: (email: string) => void;
    fetchBookedSubscriptionData: () => void;
    finishTour: (tourType: TOUR_TYPES) => void;
    startTour: (tourType: TOUR_TYPES) => void;
    doesUserHaveLowestRequiredPackage: (requiredPackage: PaymentStatusDtoSubscriptionTypeEnum, onlyActive?: boolean) => boolean;
    isImpersonating: boolean;
    setSmsTwoFactorAuthenticationData: React.Dispatch<React.SetStateAction<Partial<{ telephoneNumber: string; token: string }>>>;
    smsTwoFactorAuthenticationData: Partial<{ telephoneNumber: string; token: string }>;
    isClient: boolean;
    isFirstPaymentDone: boolean;
    renderAuthPage: number;
    userLoading: boolean;
}

export const AuthenticationContext = React.createContext({} as AuthenticationContextInterface);

export const AuthenticationProvider: React.FC = ({ children }) => {
    const [user, setUser] = useState<CurrentUserDto>({});
    const [userLoading, setUserLoading] = useState(false);
    const [signUpEmail, setSignUpEmail] = useState<string>('');
    const [signUpDialogOpen, setSignUpDialogOpen] = useState<boolean>(false);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
    const [verificationEmailSent, setVerificationEmailSent] = useState<boolean>(false);
    const [googleTwoFactorAuthenticationData, setGoogleTwoFactorAuthenticationData] = useState<Partial<GoogleAuthenticatorActivationData>>(
        {}
    );
    const [smsTwoFactorAuthenticationData, setSmsTwoFactorAuthenticationData] = useState<
        Partial<{ telephoneNumber: string; token: string }>
    >({});
    const [bookingDataLoading, setBookingDataLoading] = useState(true);
    const [bookedSubscriptionPackages, setBookedSubscriptionPackages] = useState<SubscriptionBooking>();
    const [paymentStatus, setPaymentStatus] = useState<PaymentStatusDto>();
    const [finishingWizardLoading, setFinishingWizardLoading] = useState(false);
    const [toursStatus, setToursStatus] = useState<ToursStatus>();
    const [signInCalled, setSignInCalled] = useState(false);
    const [isImpersonating, setIsImpersonating] = useState(false);
    const [renderAuthPage, setRenderAuthPage] = useState(0);

    const { pushInstruction, trackEvent } = useMatomo();

    const { showLoadingBar, hideLoadingBar, showNotifications } = useContext(AppContext);

    const removeAuthToken = () => {
        const cookie = new Cookies();
        cookie.remove(GLOBAL.JWT_COOKIE_KEY, { path: '/', domain: GLOBAL.REACT_APP_COOKIE_DOMAIN });
    };

    const setCurrentUser = async (ignoreTour?: boolean, ignoreLoading?: boolean) => {
        !ignoreLoading && setUserLoading(true);
        try {
            const response = { data: CURRENT_USER_INFO };
            pushInstruction('setUserId', response.data.id);
            setUser(response.data);
            if (!ignoreTour) {
                setToursStatus({
                    [TOUR_TYPES.MENU]: response.data.mtdWizardDone,
                    [TOUR_TYPES.MENU_MOBILE_FULL]: response.data.mtdWizardDone,
                    [TOUR_TYPES.MENU_MOBILE]: true
                });
            }
        } catch (e) {
            console.log('e', e);
        }
        !ignoreLoading && setUserLoading(false);
    };

    const setAuthToken = async (token: string | undefined = '') => {
        setUserLoading(true);
        const decodedToken: Record<string, any> = jwt_decode(token);
        setIsImpersonating(!isEmpty(decodedToken.impersonatorId));
        const cookie = new Cookies();
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + 30);
        cookie.set(GLOBAL.JWT_COOKIE_KEY, token, {
            path: '/',
            expires: expireDate,
            domain: GLOBAL.REACT_APP_COOKIE_DOMAIN
        });
    };

    const finishTour = (tourType: TOUR_TYPES) => {
        setToursStatus(prevState => ({ ...prevState, [tourType]: true }));
    };

    const startTour = (tourType: TOUR_TYPES) => {
        setToursStatus(prevState => ({ ...prevState, [tourType]: false }));
    };

    const setMtdWizardDone = async () => {
        setFinishingWizardLoading(true);
        await userApi.setMTDWizardDone();
        await setCurrentUser(true, true);
        setFinishingWizardLoading(false);
    };

    const signUp = async (user: SignUpData) => {
        showLoadingBar();
        try {
            let queryParameters = LocalStorageServices.getJSON('QUERY_PARAMETERS');
            if (queryParameters) {
                queryParameters = GlobalServices.keysToCamelCase(queryParameters);
            }
            await authenticationApi.register({ ...user, ...(queryParameters && { ...queryParameters }) });
            showNotifications({
                type: NotificationTypes.SUCCESS,
                message: Localisation.localize('SignUpScreen.successfullyRegistered')
            });
            trackEvent({
                category: 'form-event',
                name: 'SignUpWithEmailForm',
                action: 'successfully-submitted'
            });
            LocalStorageServices.removeItem('QUERY_PARAMETERS');
            setSignUpEmail(user.email);
            setConfirmationDialogOpen(false);
        } catch (e) {
            hideLoadingBar();
            trackEvent({
                category: 'form-event',
                name: 'SignUpWithEmailForm',
                action: 'submit-error'
            });
            showNotifications({ type: NotificationTypes.ERROR, message: e });
        }
    };

    const signOut = async () => {
        await removeAuthToken();
        setUser({});
        setIsImpersonating(false);
        setPaymentStatus(undefined);
        setBookedSubscriptionPackages(undefined);
        setToursStatus(undefined);
        LocalStorageServices.removeItem('twoFactorAuthenticationData');
        LocalStorageServices.removeItem('selectedChartBusinessTab');
        LocalStorageServices.removeItem(GLOBAL.MFA_TYPE);
        LocalStorageServices.removeItem(GLOBAL.MFA_TIMESTAMP);
        LocalStorageServices.removeItem(GLOBAL.MFA_REFERENCE);
        LocalStorageServices.removeItem(GLOBAL.LICENSE_KEY);
        setGoogleTwoFactorAuthenticationData({});
        setSmsTwoFactorAuthenticationData({});
    };

    const signIn = async (user: JwtRequest, history: any) => {
        showLoadingBar();
        try {
            const response = await authenticationApi.login1(user);
            const decodedToken: Record<string, any> = jwt_decode(response.data.token!);
            await setAuthToken(response.data.token);
            setSignInCalled(true);
            localStorage.setItem('initialLogin', String(response.data.initialLogin || 'false'));
            if (response.data.applicationType === JwtResponseApplicationTypeEnum.UK_SA100_STANDALONE) {
                window.location.replace(response.data.applicationUrl + '/dashboard');
            } else if (decodedToken.role === RoleTypes.AGENT) {
                window.location.replace(response.data.applicationUrl + '/account-setup/welcome');
            } else {
                if (response.data.initialLogin) {
                    history.replace('/start-trial');
                } else {
                    history.replace('/dashboard');
                }
            }
            LocalStorageServices.removeItem('twoFactorAuthenticationData');
        } catch (e) {
            if (e.twoFactorRequired) {
                LocalStorageServices.setJSON('twoFactorAuthenticationData', e.data);
            } else {
                await removeAuthToken();
                showNotifications({
                    type: NotificationTypes.ERROR,
                    message: typeof e === 'string' ? e : Localisation.localize('serverErrors.SIGN_IN_ERROR')
                });
            }
        }
        hideLoadingBar();
    };

    const handleOAuthLogin = async (provider: OAuthRequestProviderEnum, code: string, state: string, history: any) => {
        try {
            const response = await oauth2Api.login({ provider, code, state });
            await setAuthToken(response.data.token);
            localStorage.setItem('initialLogin', String(response.data.initialLogin || 'false'));
            if (response.data.initialLogin) {
                history.replace('/start-trial');
            } else {
                history.replace('/dashboard');
            }
        } catch (e) {
            if (e.twoFactorRequired) {
                LocalStorageServices.setJSON('twoFactorAuthenticationData', e.data);
            } else if (e.registeredUsingEmail) {
                history.replace('/sign-in');
                showNotifications({
                    type: NotificationTypes.ERROR,
                    message: e.message
                });
            } else {
                history.replace('/sign-in');
                showNotifications({
                    type: NotificationTypes.ERROR,
                    message: Localisation.localize('serverErrors.singleSignInError')
                });
            }
        }
    };

    const confirmTwoFactorAuthentication = async (code: string, redirectUrl: string, history: any, callbackFunction?: () => void) => {
        showLoadingBar();
        try {
            const twoFactorAuthenticationData = LocalStorageServices.getJSON('twoFactorAuthenticationData');
            await setTFAInformation(code, twoFactorAuthenticationData as TwoFactorAuthData);
            const response = await twoFactorAuthenticationApi.verifyCode(code, twoFactorAuthenticationData as TwoFactorAuthData);
            await setAuthToken(response.data.token);
            history.replace(redirectUrl);
            LocalStorageServices.removeItem('twoFactorAuthenticationData');
        } catch (e) {
            if (e.responseCode === 411) {
                console.log('e', e);
                showNotifications({ type: NotificationTypes.ERROR, message: Localisation.localize('serverErrors.SOMETHING_WENT_WRONG') });
            } else if (e.tokenErrorType === 1) {
                showNotifications({ type: NotificationTypes.ERROR, message: Localisation.localize('serverErrors.TFA_CODE_EXPIRED') });
                callbackFunction && callbackFunction();
            } else if (e.tokenErrorType === 0) {
                showNotifications({ type: NotificationTypes.ERROR, message: Localisation.localize('serverErrors.TFA_CODE_INVALID') });
            } else if (typeof e === 'string') {
                showNotifications({ type: NotificationTypes.ERROR, message: e });
            } else {
                showNotifications({ type: NotificationTypes.ERROR, message: Localisation.localize('serverErrors.SOMETHING_WENT_WRONG') });
            }
        }
        hideLoadingBar();
    };

    const setTFAInformation = async (code: string, tfaAuthData: TwoFactorAuthData) => {
        if (tfaAuthData) {
            localStorage.setItem(
                GLOBAL.MFA_TYPE,
                tfaAuthData.twoFactorAuthMethod === TwoFactorAuthDataTwoFactorAuthMethodEnum.EMAIL ? 'AUTH_CODE' : 'TOTP'
            );
        }

        localStorage.setItem(GLOBAL.MFA_TIMESTAMP, moment().utc().format('YYYY-MM-DDTHH:mm').concat('Z'));
        localStorage.setItem(GLOBAL.MFA_REFERENCE, encrypt(code, GLOBAL.AES_KEY));
    };

    const resendTwoFactorCode = async () => {
        showLoadingBar();
        try {
            const twoFactorAuthenticationData: TwoFactorAuthData = LocalStorageServices.getJSON(
                'twoFactorAuthenticationData'
            ) as TwoFactorAuthData;
            const response = await twoFactorAuthenticationApi.resendCode(twoFactorAuthenticationData);
            LocalStorageServices.setJSON('twoFactorAuthenticationData', response.data as unknown as Record<string, unknown>);
            if (response.data.twoFactorAuthMethod === TwoFactorAuthDataTwoFactorAuthMethodEnum.EMAIL) {
                showNotifications({
                    type: NotificationTypes.SUCCESS,
                    message: Localisation.localize('TFA_CODE_INPUT_SCREEN.CODE_IS_SUCCESSFULLY_SENT_EMAIL')
                });
            } else if (response.data.twoFactorAuthMethod === TwoFactorAuthDataTwoFactorAuthMethodEnum.SMS) {
                if (response.data.blockDurationInSeconds === 0) {
                    showNotifications({
                        type: NotificationTypes.SUCCESS,
                        message: Localisation.localize('TFA_CODE_INPUT_SCREEN.CODE_IS_SUCCESSFULLY_SENT_SMS')
                    });
                } else {
                    setRenderAuthPage(prevState => prevState + 1);
                }
            }
        } catch (e) {
            showNotifications({ type: NotificationTypes.ERROR, message: Localisation.localize('serverErrors.errorResendCode') });
        }
        hideLoadingBar();
    };

    const resendEmailVerificationToken = async (email: string) => {
        showLoadingBar();
        try {
            await authenticationApi.resendConfirmationLink(email);
            showNotifications({
                type: NotificationTypes.SUCCESS,
                message: Localisation.localize('ResendEmailVerificationScreen.emailSuccessfullyResent')
            });
            setVerificationEmailSent(true);
        } catch (e) {
            setVerificationEmailSent(false);
            if (e.accountAlreadyConfirmed) {
                showNotifications({ type: NotificationTypes.ERROR, message: e.message });
            } else {
                showNotifications({ type: NotificationTypes.ERROR, message: e });
            }
        }
        hideLoadingBar();
    };

    const fetchBookedSubscriptionData = async () => {
        setBookingDataLoading(true);
        await getActiveSubscriptionData();
        await getStatusOfPaymentData();
        setBookingDataLoading(false);
    };

    const getActiveSubscriptionData = async () => {
        // try {
        //     const activeSubscriptionResponse = await subscriptionControllerApi.getActiveSubscription();
        //     setBookedSubscriptionPackages(activeSubscriptionResponse.data);
        //     if (activeSubscriptionResponse.data.stripeSubscriptionId) {
        //         localStorage.setItem(GLOBAL.LICENSE_KEY, encrypt(activeSubscriptionResponse.data.stripeSubscriptionId, GLOBAL.AES_KEY));
        //     }
        // } catch (e) {
        //     localStorage.removeItem(GLOBAL.LICENSE_KEY);
        //     setBookedSubscriptionPackages(undefined);
        //     console.log(e);
        // }
    };

    const getStatusOfPaymentData = async () => {
        try {
            setPaymentStatus({
                status: PaymentStatusDtoStatusEnum.ACTIVE,
                subscriptionType: PaymentStatusDtoSubscriptionTypeEnum.PREMIUM
            });
        } catch (e) {
            setPaymentStatus(undefined);
            console.log(e);
        }
    };

    const doesUserHaveLowestRequiredPackage = (requiredPackage: PaymentStatusDtoSubscriptionTypeEnum, onlyActive?: boolean) => {
        if (paymentStatus) {
            const requiredValue: number = SubscriptionTypeValues[requiredPackage];
            const activeValue: number = SubscriptionTypeValues[paymentStatus.subscriptionType!] as unknown as number;
            if (activeValue >= requiredValue) {
                if (onlyActive) {
                    return paymentStatus.status === PaymentStatusDtoStatusEnum.ACTIVE;
                } else {
                    return (
                        paymentStatus.status === PaymentStatusDtoStatusEnum.ACTIVE ||
                        paymentStatus.status === PaymentStatusDtoStatusEnum.TRIALING
                    );
                }
            } else {
                return false;
            }
        } else {
            return false;
        }
    };

    return (
        <AuthenticationContext.Provider
            value={{
                user: CURRENT_USER_INFO,
                userLoading,
                authenticated: true,
                signUpEmail,
                signUpDialogOpen,
                confirmationDialogOpen,
                verificationEmailSent,
                googleTwoFactorAuthenticationData,
                setUser,
                setSignUpDialogOpen,
                setConfirmationDialogOpen,
                setVerificationEmailSent,
                setGoogleTwoFactorAuthenticationData,
                bookedSubscriptionPackages,
                paymentStatus,
                setPaymentStatus,
                setCurrentUser,
                setAuthToken,
                setMtdWizardDone,
                signInCalled,
                setSignInCalled,
                toursStatus,
                setToursStatus,
                finishingWizardLoading,
                signUp,
                signOut,
                signIn,
                handleOAuthLogin,
                confirmTwoFactorAuthentication,
                resendTwoFactorCode,
                resendEmailVerificationToken,
                fetchBookedSubscriptionData,
                bookingDataLoading,
                setBookingDataLoading,
                finishTour,
                startTour,
                doesUserHaveLowestRequiredPackage,
                isImpersonating,
                setSmsTwoFactorAuthenticationData,
                smsTwoFactorAuthenticationData,
                isClient: user.role?.roleName === RoleTypes.CLIENT,
                isFirstPaymentDone: Boolean(paymentStatus?.subscriptionType),
                renderAuthPage
            }}
        >
            {children}
        </AuthenticationContext.Provider>
    );
};
